import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { boolean, object, string } from "yup";
import dayjs from "dayjs";
import CustomModal from "../../shared/Modal/Modal";
import { Margin, Resolution, usePDF } from "react-to-pdf";
import { useState, useEffect } from "react";
import {
  ProctorDeclarationI,
  ProctorTableI,
  ProctorTableInstructorI,
} from "../../../interfaces/proctors.interface";
import { Alert, Button, Divider, Drawer, Input, UploadFile } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { CBCheckbox, CBDatePicker, CBInput } from "../../shared";
import CBFiles from "../../shared/CBFiles/CBFiles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateProctorSelected } from "../../../services/proctors.service";
import Message from "../../shared/Message/Message";
import { useRole } from "../../../hooks/user-roles";
import { AxiosError } from "axios";
import Course from "../../../interfaces/course.interface";

const options = {
  resolution: Resolution.HIGH,
  page: {
    margin: Margin.SMALL,
    format: "letter",
  },
  overrides: {
    pdf: {
      compress: true,
    },
    canvas: {
      useCORS: true,
    },
  },
};
interface PropsI {
  proctorSelected?: ProctorTableI | ProctorTableInstructorI;
  closeModal: () => void;
  openModal: boolean;
  course?: Course;
}

const ProctorDeclarationModal = ({
  proctorSelected,
  closeModal,
  openModal: modalOpen,
  course,
}: PropsI) => {
  const { toPDF, targetRef } = usePDF({
    ...options,
    filename: "Declaration.pdf",
    page: { margin: 15 },
  });
  const { user } = useRole();
  const queryClient = useQueryClient();
  const date = dayjs().format("MM/DD/YYYY HH:mm:ss");
  const [openModal, setOpenModal] = useState<boolean>(modalOpen);
  const [loading, setLoading] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [formInfo, setFormInfo] = useState<ProctorDeclarationI | undefined>();
  const [examCode, setExamCode] = useState<string>("");
  const [isValidExamCode, setIsValidExamCode] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const mutationOnError = (err: AxiosError) => {
    if (err.code === "ERR_BAD_REQUEST") {
      Message().error("The information provided is not right");
    } else {
      Message().error(err.message);
    }
  };
  const updateData = useMutation({
    mutationFn: (info: ProctorDeclarationI) => {
      info.end = dayjs(info.end).toISOString();
      info.start = dayjs(info.start).toISOString();
      const formData = {
        data: { id: proctorSelected?.id, ...info },
        fileList,
      };
      return updateProctorSelected(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`proctor-profile-${user?.id}-all-info`],
      });
      Message().success(
        "The exam will be available	to the instructor. Thank you for your help!",
      );
      setOpenDrawer(!openDrawer);
      setOpenModal(!openModal);
    },
    onError: mutationOnError,
  });

  const {
    control,
    handleSubmit,
    watch,
    reset: resetForm,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(
      object({
        start: string().required(),
        end: string().required(),
        place: string().required(),
        city: string().required(),
        province: string().required(),
        checked: boolean(),
      }),
    ),
    defaultValues: {
      start: proctorSelected?.start
        ? dayjs(proctorSelected?.start).format("MM/DD/YYYY HH:mm:ss")
        : date,
      end: proctorSelected?.end
        ? dayjs(proctorSelected?.end).format("MM/DD/YYYY HH:mm:ss")
        : date,
      place: proctorSelected?.place || "",
      city: proctorSelected?.city || "",
      province: proctorSelected?.province || "",
      checked: proctorSelected?.place ? true : false,
    },
  });
  const onsubmit = (data: ProctorDeclarationI) => {
    setFormInfo(data);
    setOpenDrawer(!openDrawer);
  };

  useEffect(() => {
    setOpenModal(modalOpen);
  }, [modalOpen]);
  const checked = watch("checked");
  return (
    <>
      <CustomModal
        title="Proctor Declaration Form"
        open={openModal}
        onClose={() => {
          setOpenModal(!openModal);
        }}
      >
        <form onSubmit={handleSubmit(onsubmit)}>
          <div ref={targetRef} className="p-2">
            <h1 className="text-center">
              SEABC Certificate in Structural Engineering Program Proctor
              Acceptance Form
            </h1>
            <div className="bg-slate-200 rounded p-2 text-base">
              <p>
                <span className="font-extrabold">Course:</span>
                {course
                  ? `${course.code} ${course.name}`
                  : `${proctorSelected?.assignment?.course?.code} ${proctorSelected?.assignment?.course?.name}`}
              </p>
              <p>
                <span className="font-extrabold">Term:</span>
                {course
                  ? course.term
                  : proctorSelected?.assignment?.course?.term}
              </p>
              <p>
                <span className="font-extrabold">Course Cordinator:</span>
                {course
                  ? `${proctorSelected?.assignment?.instructor.user.firstName} ${proctorSelected?.assignment?.instructor.user.lastName}`
                  : `${proctorSelected?.assignment?.courseStaff?.user?.firstName} ${proctorSelected?.assignment?.courseStaff?.user?.lastName}`}
              </p>
            </div>
            <Divider />
            <div className="text-base">
              <p>
                <span className="font-medium">Student's Name:</span>
                {`${proctorSelected?.firstName} ${proctorSelected?.lastName}`}
              </p>
              The examination was held under my supervision on
              <CBDatePicker
                control={control}
                name="start"
                className="inline-block"
                showTime={true}
                format="MM/DD/YYYY HH:mm:ss"
                disabled={!!course}
                label=""
              />
              to
              <CBDatePicker
                label=""
                control={control}
                name="end"
                className="inline-block"
                showTime={true}
                format="MM/DD/YYYY HH:mm:ss"
                disabled={!!course}
              />
              <br />
              In the office of:{" "}
              <CBInput
                label=""
                control={control}
                name="place"
                className="inline-block"
                placeholder="location"
                disabled={!!course}
              />
              <br />
              In:{" "}
              <CBInput
                label=""
                control={control}
                name="city"
                className="inline-block"
                placeholder="city"
                disabled={!!course}
              />
              ,
              <CBInput
                label=""
                control={control}
                name="province"
                className="inline-block"
                placeholder="province"
                disabled={!!course}
              />
              <br />
              <Alert
                className="text-base rounded"
                message="it is required that you shred/destroy the exam once it has been finished."
                type="error"
              />
              <label className="flex p-2 bg-amber-100 mt-2 rounded items-center">
                <CBCheckbox
                  label=""
                  name="checked"
                  control={control}
                  key="confirm-check"
                />
                By checking this box. I certify that the information stated
                above is correct and truthful, that the examinee did not have
                access to nor was made aware by me of any of the contents of the
                examination prior to its start, that the examinee received no
                aid from me or any other individual in the course of completing
                the examination, that the examinee ha no access to any material
                other than those expressly allowed for the examination, and that
                the examinee completed the examination in complete conformance
                to the instructions provided by the Course Coordinator.
              </label>
            </div>
          </div>
          <div className="flex space-x-2.5 > * + * justify-center">
            {!course && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                htmlType="submit"
                disabled={!checked}
              >
                Submit
              </Button>
            )}
            <Button
              className="box-border"
              type="primary"
              icon={<CloseOutlined />}
              danger
              onClick={() => {
                closeModal();
                resetForm();
              }}
            >
              Exit
            </Button>
            <Button
              className="bg-black hover:!bg-neutral-500"
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => toPDF()}
            >
              Print
            </Button>
          </div>
        </form>
      </CustomModal>
      <Drawer
        title={
          !isValidExamCode
            ? "You require a code to upload the written exam:"
            : `Written Exam Upload: ${proctorSelected?.assignment?.evaluationType} Instructor`
        }
        onClose={() => setOpenDrawer(!openDrawer)}
        open={openDrawer}
        className="pt-16"
      >
        {!isValidExamCode ? (
          <div className="">
            <h2>Final Exam Instructor</h2>
            <p>Enter exam code given to you by student(Case sensitive)</p>
            <Input
              placeholder="Basic usage"
              onChange={(el: any) => {
                setExamCode(el.target.value);
              }}
              value={examCode}
              prefix={
                proctorSelected?.examCode === examCode ? (
                  <CheckOutlined className="done" />
                ) : (
                  <CloseOutlined className="error" />
                )
              }
            />
          </div>
        ) : (
          <div>
            <p>
              You are uploading:
              <span className="font-bold">
                {` ${proctorSelected?.assignment?.evaluationType} `}
              </span>
              For Student:
              <span className="font-bold">
                {` ${proctorSelected?.firstName} ${proctorSelected?.lastName} `}
              </span>
            </p>
            <p>Enter exam code given to you by student(Case sensitive)</p>
            <CBFiles {...{ fileList, setFileList }} name="files" />
          </div>
        )}
        <div className="flex space-x-2.5 > * + * justify-center mt-2">
          {!isValidExamCode ? (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={proctorSelected?.examCode !== examCode}
              onClick={() => setIsValidExamCode(true)}
            >
              Confirm Code
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={fileList.length <= 0}
              onClick={() => {
                setLoading(!loading);
                if (formInfo) {
                  updateData.mutate(formInfo);
                }
              }}
              loading={loading}
            >
              Save & Submit
            </Button>
          )}
          <Button
            className="box-border"
            type="primary"
            icon={<CloseOutlined />}
            danger
            onClick={() => {
              setOpenDrawer(!openDrawer);
              setIsValidExamCode(false);
              setFormInfo(undefined);
            }}
          >
            Cancel
          </Button>
        </div>
      </Drawer>
    </>
  );
};

export default ProctorDeclarationModal;
